import { useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { Clock, ArrowLeft, Eye, EyeOff, Building2, User, Phone, Briefcase, Users, Search, CheckCircle, XCircle, Loader2, Camera, Upload, RotateCcw, X, GraduationCap, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployeeRegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Estado para busca de empresa por CNPJ
  const [cnpjSearch, setCnpjSearch] = useState("");
  const [searchingCompany, setSearchingCompany] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companySearchError, setCompanySearchError] = useState("");
  
  const [formData, setFormData] = useState({
    company_id: "",
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    cpf: "", // CPF do funcionário
    position: "",
    department: "",
    employee_type: "funcionario", // funcionario, estagiario, terceirizado
    profile_photo_url: ""
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // Estados para câmera
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Iniciar câmera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } } 
      });
      setCameraStream(stream);
      setShowCamera(true);
      
      // Aguardar o ref estar disponível
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      console.error("Erro ao acessar câmera:", error);
      toast.error("Não foi possível acessar a câmera. Verifique as permissões.");
    }
  };

  // Parar câmera
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  }, [cameraStream]);

  // Verificar qualidade da imagem (brilho e contraste)
  const checkImageQuality = (canvas) => {
    const context = canvas.getContext("2d");
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    let totalBrightness = 0;
    let pixelCount = 0;
    
    // Calcular brilho médio (amostrar cada 10 pixels para performance)
    for (let i = 0; i < data.length; i += 40) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
      totalBrightness += brightness;
      pixelCount++;
    }
    
    const avgBrightness = totalBrightness / pixelCount;
    
    if (avgBrightness < 60) {
      return { valid: false, message: "⚠️ Foto muito escura! Procure um local mais iluminado." };
    }
    if (avgBrightness > 220) {
      return { valid: false, message: "⚠️ Foto muito clara! Evite luz direta na câmera." };
    }
    
    return { valid: true, brightness: avgBrightness };
  };

  // Capturar foto da câmera
  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    
    // Configurar canvas com dimensões do vídeo
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Desenhar frame do vídeo no canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Verificar qualidade da imagem antes de aceitar
    const quality = checkImageQuality(canvas);
    if (!quality.valid) {
      toast.error(quality.message);
      return; // Mantém câmera aberta para nova tentativa
    }
    
    // Converter para blob
    canvas.toBlob(async (blob) => {
      if (!blob) {
        toast.error("Erro ao capturar foto");
        return;
      }
      
      // Preview local
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(blob);
      
      // Upload para Cloudflare
      setUploadingPhoto(true);
      stopCamera();
      
      try {
        // Converter blob para File com tipo correto
        const file = new File([blob], "profile_photo.jpg", { type: "image/jpeg" });
        
        const uploadData = new FormData();
        uploadData.append("file", file);
        
        console.log("Uploading file:", file.size, "bytes, type:", file.type);
        
        // Usar endpoint público (não requer autenticação)
        const response = await axios.post(`${API}/upload-profile-photo`, uploadData, {
          timeout: 30000
        });
        
        console.log("Upload response:", response.data);
        const photoUrl = response.data.image_url || response.data.url;
        
        if (photoUrl) {
          setFormData(prev => ({ ...prev, profile_photo_url: photoUrl }));
          toast.success("✅ Foto capturada com sucesso!");
        } else {
          toast.error("Erro: URL da foto não recebida");
          setPhotoPreview(null);
        }
      } catch (error) {
        console.error("Erro upload foto:", error);
        console.error("Error details:", error.response?.data);
        toast.error(error.response?.data?.detail || "Erro ao enviar foto");
        setPhotoPreview(null);
      } finally {
        setUploadingPhoto(false);
      }
    }, "image/jpeg", 0.9);
  };

  // Resetar foto
  const resetPhoto = () => {
    setPhotoPreview(null);
    setFormData(prev => ({ ...prev, profile_photo_url: "" }));
  };

  // Formatar CNPJ enquanto digita
  const formatCnpj = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 14) {
      return numbers
        .replace(/^(\d{2})(\d)/, "$1.$2")
        .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
        .replace(/\.(\d{3})(\d)/, ".$1/$2")
        .replace(/(\d{4})(\d)/, "$1-$2");
    }
    return value;
  };

  // Buscar empresa pelo CNPJ
  const searchCompanyByCnpj = async () => {
    if (!cnpjSearch || cnpjSearch.replace(/\D/g, "").length < 14) {
      toast.error("Digite um CNPJ válido com 14 dígitos");
      return;
    }

    setSearchingCompany(true);
    setCompanySearchError("");
    setSelectedCompany(null);

    try {
      const response = await axios.get(`${API}/companies/search`, {
        params: { cnpj: cnpjSearch }
      });
      setSelectedCompany(response.data);
      setFormData({ ...formData, company_id: response.data.id });
      toast.success(`Empresa encontrada: ${response.data.name}`);
    } catch (error) {
      const message = error.response?.data?.detail || "Empresa não encontrada";
      setCompanySearchError(message);
      setFormData({ ...formData, company_id: "" });
      toast.error(message);
    } finally {
      setSearchingCompany(false);
    }
  };

  // Limpar empresa selecionada
  const clearSelectedCompany = () => {
    setSelectedCompany(null);
    setCnpjSearch("");
    setCompanySearchError("");
    setFormData({ ...formData, company_id: "" });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    if (!formData.company_id) {
      toast.error("Busque e selecione a empresa onde você trabalha pelo CNPJ");
      return;
    }
    
    if (!formData.profile_photo_url) {
      toast.error("A foto de perfil é obrigatória!");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/employees/register`, {
        company_id: formData.company_id,
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        position: formData.position,
        department: formData.department,
        employee_type: formData.employee_type,
        profile_photo_url: formData.profile_photo_url
      });

      // Salvar token e fazer login automático
      localStorage.setItem("token", response.data.token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${response.data.token}`;
      
      toast.success("Cadastro realizado com sucesso!");
      
      // Recarregar a página para atualizar o contexto de auth
      window.location.href = "/dashboard";
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao cadastrar";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="p-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/login" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Voltar ao login
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg">
          <div className="text-center mb-6">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <img 
                src="/logo-control-isp.png" 
                alt="CiaPonto" 
                className="h-12 w-auto"
              />
            </Link>
            <h1 className="font-heading font-bold text-2xl text-slate-900 mt-4">Cadastro de Funcionário</h1>
            <p className="text-slate-600">Preencha seus dados para acessar o sistema</p>
          </div>

          <Card className="border-2 border-green-500 shadow-lg">
            <CardHeader className="pb-4">
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-2">
                <User className="w-7 h-7 text-green-600" />
              </div>
              <CardTitle className="font-heading text-xl text-center">Seus Dados</CardTitle>
              <CardDescription className="text-center">
                Cadastre-se na empresa onde você trabalha
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Busca da Empresa por CNPJ */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-500" />
                    Encontre sua empresa pelo CNPJ
                  </Label>
                  
                  {!selectedCompany ? (
                    <>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Digite o CNPJ da empresa (ex: 00.000.000/0000-00)"
                          value={cnpjSearch}
                          onChange={(e) => setCnpjSearch(formatCnpj(e.target.value))}
                          maxLength={18}
                          className="flex-1"
                          data-testid="cnpj-search-input"
                        />
                        <Button 
                          type="button" 
                          onClick={searchCompanyByCnpj}
                          disabled={searchingCompany || cnpjSearch.replace(/\D/g, "").length < 14}
                          className="bg-green-500 hover:bg-green-600"
                          data-testid="search-company-btn"
                        >
                          {searchingCompany ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Search className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      
                      {companySearchError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-red-700 font-medium">Empresa não encontrada</p>
                            <p className="text-xs text-red-600 mt-1">
                              Verifique se o CNPJ está correto e se a empresa está cadastrada no sistema CiaPonto.
                            </p>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-slate-500">
                        Digite o CNPJ da empresa onde você trabalha para encontrá-la no sistema.
                      </p>
                    </>
                  ) : (
                    <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-green-800">{selectedCompany.name}</p>
                            <p className="text-sm text-green-600">CNPJ: {selectedCompany.cnpj}</p>
                          </div>
                        </div>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={clearSelectedCompany}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          Trocar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Foto de Perfil via Câmera */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Camera className="w-4 h-4 text-slate-500" />
                    Foto de Perfil *
                  </Label>
                  
                  {/* Modal de Câmera com Instruções de Qualidade */}
                  {showCamera && (
                    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                      <div className="bg-white rounded-2xl p-4 w-full max-w-md">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-medium text-slate-900">Tire sua foto de perfil</h3>
                          <button onClick={stopCamera} className="p-1 hover:bg-slate-100 rounded-full">
                            <X className="w-5 h-5 text-slate-500" />
                          </button>
                        </div>
                        
                        {/* Instruções de qualidade */}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                          <p className="text-xs font-medium text-amber-800 mb-2">📸 Para uma foto válida:</p>
                          <ul className="text-xs text-amber-700 space-y-1">
                            <li>✓ Esteja em um <strong>local bem iluminado</strong></li>
                            <li>✓ Mantenha o <strong>rosto centralizado</strong> no círculo</li>
                            <li>✓ Evite sombras no rosto</li>
                            <li>✓ Olhe diretamente para a câmera</li>
                            <li>✓ Remova óculos escuros ou bonés</li>
                          </ul>
                        </div>
                        
                        <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
                          <video 
                            ref={videoRef}
                            autoPlay 
                            playsInline 
                            muted
                            className="w-full h-full object-cover transform scale-x-[-1]"
                          />
                          {/* Guia de enquadramento */}
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-48 h-48 border-4 border-white/50 rounded-full">
                              <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-white/90 px-2 py-1 rounded text-xs font-medium">
                                Enquadre seu rosto aqui
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-sm text-slate-600 text-center my-3">
                          Esta foto será comparada com as fotos do registro de ponto
                        </p>
                        
                        <Button 
                          onClick={capturePhoto}
                          className="w-full bg-sky-500 hover:bg-sky-600"
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          Capturar Foto
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Canvas oculto para captura */}
                  <canvas ref={canvasRef} className="hidden" />
                  
                  {/* Preview e botão de câmera */}
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {photoPreview || formData.profile_photo_url ? (
                        <img 
                          src={photoPreview || formData.profile_photo_url} 
                          alt="Preview" 
                          className="w-24 h-24 rounded-full object-cover border-4 border-sky-500 shadow-lg"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center border-4 border-dashed border-slate-300">
                          <Camera className="w-10 h-10 text-slate-400" />
                        </div>
                      )}
                      {uploadingPhoto && (
                        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-3 border-white border-t-transparent"></div>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      {!photoPreview && !formData.profile_photo_url ? (
                        <Button 
                          type="button"
                          onClick={startCamera}
                          className="bg-sky-500 hover:bg-sky-600"
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          Abrir Câmera
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button 
                            type="button"
                            onClick={startCamera}
                            variant="outline"
                            size="sm"
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Nova foto
                          </Button>
                          <Button 
                            type="button"
                            onClick={resetPhoto}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Remover
                          </Button>
                        </div>
                      )}
                      <p className="text-xs text-slate-500">
                        📷 Tire uma foto real usando a câmera do dispositivo
                      </p>
                      <p className="text-xs text-amber-600">
                        Esta foto será usada para verificação de ponto
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Nome */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-500" />
                    Nome completo
                  </Label>
                  <Input
                    name="name"
                    placeholder="Seu nome completo"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="employee-name-input"
                  />
                </div>

                {/* Email e Telefone */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      name="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="employee-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-500" />
                      Telefone
                    </Label>
                    <Input
                      name="phone"
                      placeholder="(00) 00000-0000"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="employee-phone-input"
                    />
                  </div>
                </div>

                {/* CPF */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-500" />
                    CPF
                  </Label>
                  <Input
                    name="cpf"
                    placeholder="000.000.000-00"
                    value={formData.cpf}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 11) {
                        value = value.replace(/(\d{3})(\d)/, '$1.$2');
                        value = value.replace(/(\d{3})(\d)/, '$1.$2');
                        value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                      }
                      setFormData({ ...formData, cpf: value });
                    }}
                    maxLength={14}
                    data-testid="employee-cpf-input"
                  />
                </div>

                {/* Cargo e Departamento */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-slate-500" />
                      Cargo
                    </Label>
                    <Input
                      name="position"
                      placeholder="Ex: Técnico"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      data-testid="employee-position-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-500" />
                      Departamento
                    </Label>
                    <Input
                      name="department"
                      placeholder="Ex: Suporte"
                      value={formData.department}
                      onChange={handleChange}
                      required
                      data-testid="employee-department-input"
                    />
                  </div>
                </div>

                {/* Tipo de Colaborador */}
                <div className="space-y-2">
                  <Label className="text-base font-medium flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-slate-500" />
                    Tipo de Colaborador
                  </Label>
                  <div className="grid grid-cols-3 gap-3">
                    <label 
                      className={`flex items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.employee_type === "funcionario" 
                          ? "border-sky-500 bg-sky-50 text-sky-700" 
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="employee_type"
                        value="funcionario"
                        checked={formData.employee_type === "funcionario"}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <Users className="w-5 h-5 mx-auto mb-1" />
                        <span className="text-sm font-medium">Funcionário</span>
                      </div>
                    </label>
                    <label 
                      className={`flex items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.employee_type === "estagiario" 
                          ? "border-green-500 bg-green-50 text-green-700" 
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="employee_type"
                        value="estagiario"
                        checked={formData.employee_type === "estagiario"}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <GraduationCap className="w-5 h-5 mx-auto mb-1" />
                        <span className="text-sm font-medium">Estagiário</span>
                      </div>
                    </label>
                    <label 
                      className={`flex items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.employee_type === "terceirizado" 
                          ? "border-orange-500 bg-orange-50 text-orange-700" 
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="employee_type"
                        value="terceirizado"
                        checked={formData.employee_type === "terceirizado"}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <Briefcase className="w-5 h-5 mx-auto mb-1" />
                        <span className="text-sm font-medium">Terceirizado</span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Senhas */}
                <div className="space-y-2">
                  <Label>Senha</Label>
                  <div className="relative">
                    <Input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Mínimo 6 caracteres"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={6}
                      data-testid="employee-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Confirmar senha</Label>
                  <Input
                    name="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Digite a senha novamente"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    data-testid="employee-confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-green-500 hover:bg-green-600 h-11"
                  disabled={submitting || !formData.company_id}
                  data-testid="employee-register-submit-btn"
                >
                  {submitting ? "Cadastrando..." : "Criar minha conta"}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <p className="text-sm text-slate-600">
                  Já tem uma conta?{" "}
                  <Link to="/login" className="text-green-600 hover:text-green-700 font-medium">
                    Fazer login
                  </Link>
                </p>
              </div>

              <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-100">
                <p className="text-xs text-green-700 text-center">
                  Ao se cadastrar, você terá acesso para registrar seu ponto e consultar suas horas trabalhadas.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
